import { getSessionToken } from '../lib/auth.js';

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export type PersonaLineIssue = {
  index: number;
  matched: string;
  text: string;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly field?: string,
    readonly issues?: PersonaLineIssue[],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuthRequired(): boolean {
    return this.status === 401;
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getSessionToken();
  const headers: Record<string, string> = {
    ...(init.body ? { 'content-type': 'application/json' } : {}),
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${BASE}${path}`, { ...init, headers });
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = text;
  }

  if (!response.ok) {
    const payload = body as {
      error?: string | { message?: string };
      field?: string;
      issues?: PersonaLineIssue[];
    } | undefined;
    const err = payload?.error;
    const message =
      typeof err === 'string'
        ? err
        : err && typeof err === 'object' && typeof err.message === 'string'
          ? err.message
          : `Request failed with ${response.status}.`;
    throw new ApiError(
      response.status,
      message,
      typeof payload?.field === 'string' ? payload.field : undefined,
      Array.isArray(payload?.issues) ? payload.issues : undefined,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'POST', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
  put: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'PUT', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
  patch: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'PATCH', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
  delete: <T>(path: string) => call<T>(path, { method: 'DELETE' }),
};

export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}
